import { Check } from 'lucide-react';
import { PricingTier } from '../types';
import { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/axios';

const tiers: PricingTier[] = [
  {
    name: 'Free',
    price: 0,
    priceId: 'price_free',
    description: 'Everything you need to find the best link deals',
    features: [
      'Unlimited domain lookups',
      'Full database browser',
      'Advanced filters (DR, price, country)',
      'CSV + JSON export',
      'Unlimited saved lists',
      'Unlimited history',
      'Price alerts',
      'Competitor backlink analysis',
      'Seller trust scores',
      'Price trend insights',
    ],
    cta: 'Get Started Free',
  },
  {
    name: 'API Access',
    price: 99,
    priceId: 'price_enterprise',
    description: 'For agencies and tools that need programmatic access',
    features: [
      'Everything in Free',
      'REST API access',
      'API keys',
      'Bulk endpoints (5,000 domains/request)',
      'Webhook callbacks',
      'Priority support',
      'Custom integrations',
      'Zapier / Make compatible',
    ],
    popular: true,
    cta: 'Start API Access',
  },
];

export default function PricingTable() {
  const [loading, setLoading] = useState<string | null>(null);
  const { isAuthenticated } = useAuthStore();

  const handleSubscribe = async (priceId: string, tierName: string) => {
    if (priceId === 'price_free') {
      window.location.href = '/register';
      return;
    }

    if (!isAuthenticated) {
      window.location.href = '/register';
      return;
    }

    setLoading(priceId);

    try {
      const response = await api.post('/api/billing/checkout', {
        price_id: priceId,
        success_url: `${window.location.origin}/billing?success=true`,
        cancel_url: `${window.location.origin}/billing?canceled=true`,
      });

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
      {tiers.map((tier) => (
        <div
          key={tier.name}
          className={`relative rounded-2xl border ${
            tier.popular
              ? 'border-brand-500 shadow-xl'
              : 'border-gray-200 dark:border-gray-700'
          } bg-white dark:bg-gray-800 p-8 flex flex-col`}
        >
          {tier.popular && (
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="bg-brand-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                For Agencies
              </span>
            </div>
          )}

          <div className="mb-8">
            <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{tier.description}</p>
            <div className="flex items-baseline">
              <span className="text-5xl font-bold">{tier.price === 0 ? 'Free' : `$${tier.price}`}</span>
              {tier.price > 0 && <span className="text-gray-600 dark:text-gray-400 ml-2">/month</span>}
            </div>
          </div>

          <ul className="space-y-4 mb-8 flex-grow">
            {tier.features.map((feature) => (
              <li key={feature} className="flex items-start">
                <Check className="h-5 w-5 text-brand-500 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-300">{feature}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={() => handleSubscribe(tier.priceId, tier.name)}
            disabled={loading === tier.priceId}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
              tier.popular
                ? 'bg-brand-500 text-white hover:bg-brand-600'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading === tier.priceId ? 'Loading...' : tier.cta}
          </button>
        </div>
      ))}
    </div>
  );
}
